import React from 'react';
import { Box, Typography, Link, List, ListItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import heroImage from '../../assets/knowledgebase-hero.jpg';

// ── Hero ─────────────────────────────────────────────────────────────────────

const HeroSection = styled(Box)({
  position: 'relative',
  height: 280,
  overflow: 'hidden',
  flexShrink: 0,
});

const HeroImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center 40%',
  filter: 'saturate(2.0) contrast(1.2) brightness(1.08) hue-rotate(-10deg)',
});

const HeroOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to bottom, rgba(10,30,60,0.50) 0%, rgba(10,30,60,0.72) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: '0 48px 0 180px',
  gap: 12,
});

// ── Nav buttons ───────────────────────────────────────────────────────────────

const NavButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#3b5bdb',
  color: '#fff',
  borderRadius: 10,
  padding: theme.spacing(2, 3),
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'background-color 0.15s',
  '&:hover': {
    backgroundColor: '#2f4abf',
  },
}));

// ── Content sections ──────────────────────────────────────────────────────────

const SectionWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#dde1e7',
  borderRadius: 10,
  padding: theme.spacing(3, 4),
  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
}));

const ResourceLink = styled(Link)({
  color: '#4a8fd4',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

// ── Data ──────────────────────────────────────────────────────────────────────

const navItems = [
  { id: 'billing', label: 'Billing and Account Setup' },
  { id: 'data', label: 'Using Your Data: Reports, Analyses, and More' },
  { id: 'platform', label: 'Using the CarbonSpace Platform' },
  { id: 'technology', label: 'About Our Technology' },
];

const sections = [
  {
    id: 'billing',
    title: 'Billing and Account Setup',
    description: 'Please reference the resources below for more information about your CarbonSpace billing and account setup:',
    links: [
      { label: 'CarbonSpace Billing and Invoicing Process', url: 'https://hubs.ly/Q047DdHF0' },
      { label: 'Account Setup and Project Initiation (CarbonSpace FAQ)', url: 'https://hubs.ly/Q02_1YG40' },
    ],
  },
  {
    id: 'data',
    title: 'Using Your Data: Reports, Analyses and More',
    description: 'Please reference the resources below for more information about CarbonSpace data, use cases, and deliverables:',
    links: [
      { label: 'NEE Use Cases: how can you use and benefit from CarbonSpace data?', url: 'https://carbonspace.gitbook.io/carbonspace-knowledge-base' },
      { label: 'Using My Data: Reports, Claims, Carbon Credits, LCAs (CarbonSpace FAQ)', url: 'https://hubs.ly/Q02_1YG40' },
      { label: 'CarbonSpace Approach: How it works', url: 'https://carbonspace.tech/how-it-works/our-approach' },
    ],
  },
  {
    id: 'platform',
    title: 'Using the CarbonSpace Platform',
    description: "Please reference the resources below about using CarbonSpace's UI:",
    links: [
      { label: 'User Interface Guide (Jan 2026)', url: 'https://app-na1.hubspotdocuments.com/documents/8110409/view/1586517776?accessId=20f09f' },
      { label: 'Video: How to Use the Compare Groups Tool', url: 'https://www.loom.com/share/ca1f85b1132245f2964ab548e8dca564' },
      { label: 'Video: How to Use the Historical Baseline Tool', url: 'https://www.loom.com/share/791af7b07c824e9d9b995b88b068753c' },
      { label: 'CarbonSpace FAQ', url: 'https://app-na1.hubspotdocuments.com/documents/8110409/view/977929981?accessId=eed8ef' },
    ],
  },
  {
    id: 'technology',
    title: 'About Our Technology',
    description: "Please reference the resources below for more information about CarbonSpace's technology:",
    links: [
      { label: 'Overview: Our Technology', url: 'https://carbonspace.tech/how-it-works/technology' },
      { label: 'Peer-Reviewed Publication: Globally Scalable Approach to Estimate Net Ecosystem Exchange Based on Remote Sensing, Meteorological Data, and Direct Measurements of Eddy Covariance Sites', url: 'https://www.mdpi.com/2072-4292/14/21/5529' },
      { label: "White Paper: CarbonSpace's Technology, Science, and Applications in the Evolving Landscape of Carbon MRV Solutions", url: 'https://hubs.ly/Q047Dlb20' },
      { label: 'Read our FAQs for more information about our technology', url: 'https://app-na1.hubspotdocuments.com/documents/8110409/view/977929981?accessId=eed8ef' },
    ],
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

const KnowledgeBaseView: React.FC = () => {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ height: '100vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

      {/* Hero */}
      <HeroSection>
        <HeroImage src={heroImage} alt="Aerial forest view" />
        <HeroOverlay>
          <Typography variant="h4" fontWeight={700} color="white" sx={{ lineHeight: 1.25 }}>
            CarbonSpace Customer<br />Knowledge Base
          </Typography>
          <Typography variant="body1" color="rgba(255,255,255,0.85)">
            Please review the resources below and reach out to{' '}
            <Link href="mailto:support@carbonspace.tech" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 500 }}>
              support@carbonspace.tech
            </Link>{' '}
            if you<br />have any additional questions.
          </Typography>
        </HeroOverlay>
      </HeroSection>

      {/* Body */}
      <Box sx={{ px: 4, py: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Content sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, pb: 6 }}>
          {sections.map((section) => (
            <SectionWrapper key={section.id} id={section.id}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                {section.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {section.description}
              </Typography>
              <List dense disablePadding sx={{ listStyleType: 'disc', pl: 2.5 }}>
                {section.links.map((link) => (
                  <ListItem key={link.url} disablePadding sx={{ display: 'list-item', mb: 0.5 }}>
                    <ResourceLink href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.label}
                    </ResourceLink>
                  </ListItem>
                ))}
              </List>
            </SectionWrapper>
          ))}
        </Box>

      </Box>
    </Box>
  );
};

export default KnowledgeBaseView;